import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  IconButton,
  Alert,
  AlertTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  PROPERTY_TYPE_OPTIONS,
  GUARANTEE_TYPE_OPTIONS,
  GUARANTEE_TYPE_INFO,
  MONTHS_OPTIONS,
  ROLE_OPTIONS,
  PERSON_TYPE_OPTIONS,
  ADDITIONAL_OPTIONS,
} from '@/utils/seagFormFields.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useSubmitSeagForm } from '@/lib/api/partner-request.ts';
import { useNavigate } from 'react-router-dom';
import { validateDNI, validateNIE } from '@/utils/misc.ts';
import DocumentField from '@/components/form/DocumentField.tsx';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';

interface Person {
  role: string;
  personType: string;
  name: string;
  surname1: string;
  surname2: string;
  documentType: string;
  documentNumber: string;
  phone1: string;
  phone2: string;
  email: string;
  isSelfEmployed: boolean;
  payslipDocument?: File;
  idDocument?: File;
  taxDeclarationDocument?: File;
  selfEmployedDocument?: File;
}

interface FormData {
  isRented: string;
  propertyType: string;
  guaranteeType: string;
  price: number | null;
  deposit: string;
  advance: string;
  landlordDocument: string;
  options: string[];
  propertyAddress: string;
  persons: Person[];
  comments: string;
  privacy: boolean;
}

const SeagForm = () => {
  const { show: showSnackbar } = useSnackbar();
  const { mutateAsync, isPending } = useSubmitSeagForm();
  const navigate = useNavigate();
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    isRented: '',
    propertyType: '',
    guaranteeType: '',
    price: null,
    deposit: '',
    advance: '',
    landlordDocument: '',
    options: [],
    propertyAddress: '',
    persons: [
      {
        role: '',
        personType: '',
        name: '',
        surname1: '',
        surname2: '',
        documentType: '',
        documentNumber: '',
        phone1: '',
        phone2: '',
        email: '',
        isSelfEmployed: false,
      },
    ],
    comments: '',
    privacy: false,
  });

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | string[] | number | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addPerson = () => {
    if (formData.persons.length >= 15) {
      showSnackbar({
        title: 'Máximo 15 personas (12 inquilinos + 3 avalistas)',
        variant: 'warning',
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      persons: [
        ...prev.persons,
        {
          role: '',
          personType: '',
          name: '',
          surname1: '',
          surname2: '',
          documentType: '',
          documentNumber: '',
          phone1: '',
          phone2: '',
          email: '',
          isSelfEmployed: false,
        },
      ],
    }));
  };

  const removePerson = (index: number) => {
    if (formData.persons.length === 1) {
      showSnackbar({ title: 'Debe haber al menos una persona', variant: 'warning' });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      persons: prev.persons.filter((_, i) => i !== index),
    }));
  };

  const updatePerson = (index: number, field: keyof Person, value: string | boolean | File) => {
    setFormData((prev) => ({
      ...prev,
      persons: prev.persons.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));
  };

  const handleFileChange = (index: number, field: keyof Person, file: File | undefined) => {
    if (file) {
      updatePerson(index, field, file);
    }
  };

  const validateDocumentNumber = (type: string, number: string): boolean => {
    if (type === 'DNI') return validateDNI(number);
    if (type === 'NIE') return validateNIE(number);
    if (type === 'Pasaporte') return number.trim() !== '';
    return false;
  };

  const handleCheckboxChange = (option: string) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.includes(option)
        ? prev.options.filter((o) => o !== option)
        : [...prev.options, option],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacy) {
      showSnackbar({ title: 'Debes aceptar la política de privacidad', variant: 'error' });
      return;
    }

    const requiredFields = [
      'isRented',
      'propertyType',
      'guaranteeType',
      'price',
      'deposit',
      'advance',
      'propertyAddress',
    ];
    const emptyFields = requiredFields.filter((field) => !formData[field as keyof FormData]);

    if (emptyFields.length > 0) {
      showSnackbar({
        title: 'Completa todos los campos obligatorios del inmueble',
        variant: 'error',
      });
      return;
    }

    for (let i = 0; i < formData.persons.length; i++) {
      const p = formData.persons[i];
      if (
        !p.role ||
        !p.personType ||
        !p.name ||
        !p.surname1 ||
        !p.documentType ||
        !p.documentNumber ||
        !p.phone1 ||
        !p.email
      ) {
        showSnackbar({
          title: `Completa todos los campos obligatorios de la persona ${i + 1}`,
          variant: 'error',
        });
        return;
      }
      if (!validateDocumentNumber(p.documentType, p.documentNumber)) {
        showSnackbar({
          title: `El documento de la persona ${i + 1} no es válido`,
          variant: 'error',
        });
        return;
      }
      if (!p.idDocument || !p.payslipDocument) {
        showSnackbar({ title: `Sube DNI y nómina de la persona ${i + 1}`, variant: 'error' });
        return;
      }
      if (p.isSelfEmployed && (!p.taxDeclarationDocument || !p.selfEmployedDocument)) {
        showSnackbar({
          title: `Sube declaración de impuestos y recibo de autónomos de la persona ${i + 1}`,
          variant: 'error',
        });
        return;
      }
    }

    const formDataToSend = new FormData();
    formDataToSend.append('isRented', formData.isRented);
    formDataToSend.append('propertyType', formData.propertyType);
    formDataToSend.append('guaranteeType', formData.guaranteeType);
    formDataToSend.append('price', String(formData.price ?? ''));
    formDataToSend.append('deposit', formData.deposit);
    formDataToSend.append('advance', formData.advance);
    formDataToSend.append('landlordDocument', formData.landlordDocument);
    formDataToSend.append('options', JSON.stringify(formData.options));
    formDataToSend.append('propertyAddress', formData.propertyAddress);
    formDataToSend.append('comments', formData.comments);

    formData.persons.forEach((person, index) => {
      formDataToSend.append(`persons[${index}][role]`, person.role);
      formDataToSend.append(`persons[${index}][personType]`, person.personType);
      formDataToSend.append(`persons[${index}][name]`, person.name);
      formDataToSend.append(`persons[${index}][surname1]`, person.surname1);
      formDataToSend.append(`persons[${index}][surname2]`, person.surname2);
      formDataToSend.append(`persons[${index}][documentType]`, person.documentType);
      formDataToSend.append(`persons[${index}][documentNumber]`, person.documentNumber);
      formDataToSend.append(`persons[${index}][phone1]`, person.phone1);
      formDataToSend.append(`persons[${index}][phone2]`, person.phone2);
      formDataToSend.append(`persons[${index}][email]`, person.email);
      formDataToSend.append(`persons[${index}][isSelfEmployed]`, String(person.isSelfEmployed));

      if (person.idDocument) {
        formDataToSend.append(`persons[${index}][idDocument]`, person.idDocument);
      }
      if (person.payslipDocument) {
        formDataToSend.append(`persons[${index}][payslipDocument]`, person.payslipDocument);
      }
      if (person.taxDeclarationDocument) {
        formDataToSend.append(
          `persons[${index}][taxDeclarationDocument]`,
          person.taxDeclarationDocument
        );
      }
      if (person.selfEmployedDocument) {
        formDataToSend.append(
          `persons[${index}][selfEmployedDocument]`,
          person.selfEmployedDocument
        );
      }
    });

    await mutateAsync(formDataToSend);
    showSnackbar({
      title: 'Formulario enviado correctamente',
      message: 'En breve nos pondremos en contacto contigo',
      variant: 'success',
    });
    navigate('/partners');
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Formulario SEAG
      </Typography>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Datos del inmueble
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" required>
              <FormLabel>¿El inmueble ya está alquilado?</FormLabel>
              <RadioGroup
                row
                value={formData.isRented}
                onChange={(e) => handleChange('isRented', e.target.value)}
              >
                <FormControlLabel value="si" control={<Radio required />} label="Sí" />
                <FormControlLabel value="no" control={<Radio required />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Tipo de inmueble"
              value={formData.propertyType}
              onChange={(e) => handleChange('propertyType', e.target.value)}
              required
            >
              {PROPERTY_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Tipos de garantía disponibles</AlertTitle>
              {GUARANTEE_TYPE_INFO.map((info) => (
                <Box key={info.value} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                  <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                    {info.value}:
                  </Typography>{' '}
                  <Typography variant="body2" component="span">
                    {info.description}
                  </Typography>{' '}
                  <Link
                    href={info.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.3,
                      fontSize: '0.875rem',
                    }}
                  >
                    Más info
                    <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                  </Link>
                </Box>
              ))}
            </Alert>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Tipo de garantía"
              value={formData.guaranteeType}
              onChange={(e) => handleChange('guaranteeType', e.target.value)}
              required
            >
              {GUARANTEE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <EurCurrencyField
              label="Precio de alquiler"
              value={formData.price}
              onChange={(value) => handleChange('price', value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Meses de depósito"
              value={formData.deposit}
              onChange={(e) => handleChange('deposit', e.target.value)}
              required
            >
              {MONTHS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Meses de adelanto"
              value={formData.advance}
              onChange={(e) => handleChange('advance', e.target.value)}
              required
            >
              {MONTHS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="DNI/CIF del arrendador/a (opcional)"
              value={formData.landlordDocument}
              onChange={(e) => handleChange('landlordDocument', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel>Más opciones</FormLabel>
              {ADDITIONAL_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={formData.options.includes(option)}
                      onChange={() => handleCheckboxChange(option)}
                    />
                  }
                  label={option}
                />
              ))}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Dirección completa del inmueble"
              value={formData.propertyAddress}
              onChange={(e) => handleChange('propertyAddress', e.target.value)}
              required
            />
          </Grid>
        </Grid>
      </Paper>

      {formData.persons.map((person, index) => (
        <Paper key={index} elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Persona {index + 1}</Typography>
            {formData.persons.length > 1 && (
              <IconButton onClick={() => removePerson(index)} color="error">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Rol"
                value={person.role}
                onChange={(e) => updatePerson(index, 'role', e.target.value)}
                required
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo de persona"
                value={person.personType}
                onChange={(e) => updatePerson(index, 'personType', e.target.value)}
                required
              >
                {PERSON_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nombre"
                value={person.name}
                onChange={(e) => updatePerson(index, 'name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Primer apellido"
                value={person.surname1}
                onChange={(e) => updatePerson(index, 'surname1', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Segundo apellido"
                value={person.surname2}
                onChange={(e) => updatePerson(index, 'surname2', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <DocumentField
                documentType={person.documentType as 'DNI' | 'NIE' | 'Pasaporte'}
                value={person.documentNumber}
                onValueChange={(val) => updatePerson(index, 'documentNumber', val)}
                onTypeChange={(type) => updatePerson(index, 'documentType', type)}
                helperTextValue=""
                errorValue={false}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono 1"
                value={person.phone1}
                onChange={(e) => updatePerson(index, 'phone1', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono 2 (opcional)"
                value={person.phone2}
                onChange={(e) => updatePerson(index, 'phone2', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={person.email}
                onChange={(e) => updatePerson(index, 'email', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Documentos requeridos
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label" fullWidth>
                {person.idDocument
                  ? `DNI/NIE/Pasaporte: ${person.idDocument.name}`
                  : 'Subir DNI/NIE/Pasaporte *'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(index, 'idDocument', e.target.files?.[0])}
                />
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label" fullWidth>
                {person.payslipDocument
                  ? `Última nómina: ${person.payslipDocument.name}`
                  : 'Subir última nómina *'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(index, 'payslipDocument', e.target.files?.[0])}
                />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={person.isSelfEmployed}
                    onChange={(e) => updatePerson(index, 'isSelfEmployed', e.target.checked)}
                  />
                }
                label="Es autónomo"
              />
            </Grid>

            {person.isSelfEmployed && (
              <>
                <Grid item xs={12} md={6}>
                  <Button variant="outlined" component="label" fullWidth>
                    {person.taxDeclarationDocument
                      ? `Declaración impuestos: ${person.taxDeclarationDocument.name}`
                      : 'Subir declaración de impuestos *'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileChange(index, 'taxDeclarationDocument', e.target.files?.[0])
                      }
                    />
                  </Button>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button variant="outlined" component="label" fullWidth>
                    {person.selfEmployedDocument
                      ? `Recibo autónomos: ${person.selfEmployedDocument.name}`
                      : 'Subir recibo de autónomos *'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileChange(index, 'selfEmployedDocument', e.target.files?.[0])
                      }
                    />
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addPerson}
        fullWidth
        sx={{ mb: 3 }}
        disabled={formData.persons.length >= 15}
      >
        Añadir persona ({formData.persons.length}/15)
      </Button>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Comentarios adicionales
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Comentarios (opcional)"
          placeholder="Escribe aquí cualquier información adicional que quieras transmitir..."
          value={formData.comments}
          onChange={(e) => handleChange('comments', e.target.value)}
        />
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.privacy}
              onChange={(e) => handleChange('privacy', e.target.checked)}
              required
            />
          }
          label={
            <Typography variant="body2" component="span">
              Acepto que mis datos sean compartidos con SEAG para tramitar mi solicitud de seguro de
              impago.{' '}
              <Link
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyDialogOpen(true);
                }}
                sx={{ cursor: 'pointer' }}
              >
                Ver más información
              </Link>
            </Typography>
          }
        />
        {!formData.privacy && (
          <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
            Debes aceptar para continuar
          </Typography>
        )}
      </Paper>

      <Button type="submit" variant="contained" size="large" fullWidth disabled={isPending}>
        {isPending ? 'Enviando...' : 'Enviar solicitud'}
      </Button>

      <Dialog
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Información sobre el tratamiento de datos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Al enviar este formulario, tus datos personales serán compartidos con SEAG (Sociedad
            Española de Avales y Garantías) para tramitar tu solicitud de seguro de impago de
            alquiler.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Datos que se compartirán:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            • Datos del inmueble (tipo, precio, dirección, etc.)
            <br />
            • Datos de inquilinos y avalistas (nombre, documento, teléfono, email)
            <br />• Información sobre el rol y tipo de persona
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Finalidad:</strong> Tramitar tu solicitud de seguro de impago con SEAG.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Base legal:</strong> Consentimiento del interesado (RGPD Art. 6.1.a).
          </Typography>
          <Typography variant="body2">
            Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación,
            portabilidad y oposición contactando con hola@inversorhub.com
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyDialogOpen(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeagForm;
